import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import { driverAvailabilityApi } from "@/lib/entities";
import { apiError } from "@/lib/errors";
import { useAuth } from "@/contexts/AuthContext";

/**
 * DAQ-1 Sub-Phase 3 · Driver Availability tab.
 *
 * Renders the canonical `driver_availability_windows` for one driver.
 * All rendering pulls status directly from the API rows (no local date
 * math) so the eligibility engine remains the single source of truth
 * for what the operator sees elsewhere.
 *
 * Permission gating is enforced backend-side (`drivers.availability`);
 * we hide write controls if the caller lacks the permission — the
 * backend still rejects any write, so this is UX polish only.
 */

const WRITE_ROLES = ["super_admin", "admin", "ops_dispatch"];

const STATUS_STYLES = {
  confirmed_available: {
    label: "Confirmed Available",
    cls: "bg-touring-lime/15 text-carbon border-touring-lime/50",
  },
  unavailable: {
    label: "Unavailable",
    cls: "bg-critical-red/10 text-critical-red border-critical-red/40",
  },
};

const SOURCE_LABEL = {
  operator: "Operator",
  driver_self: "Driver Self-Reported",
  import: "Imported",
};

function StatusChip({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.confirmed_available;
  return (
    <span className={`inline-flex items-center border rounded-sm px-1.5 py-0.5 text-[10.5px] uppercase tracking-widest ${s.cls}`}>
      {s.label}
    </span>
  );
}

export default function DriverAvailabilityTab({ driverId }) {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null); // window row being edited
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const r = await driverAvailabilityApi.list(driverId);
      // Newest window first.
      r.sort((a, b) => (b.start_date || "").localeCompare(a.start_date || ""));
      setRows(r);
    } catch (e) {
      toast.error(apiError(e) || "Failed to load availability");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [driverId]);

  const remove = async (row) => {
    if (!window.confirm(`Delete availability window ${row.start_date} → ${row.end_date}?`)) return;
    try {
      await driverAvailabilityApi.archive(driverId, row.id);
      toast.success("Availability window removed");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-3" data-testid="driver-availability-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[11.5px] text-steel max-w-2xl leading-relaxed">
          Availability windows are the operator's affirmative statements about when this driver is available or unavailable.
          Absence of a row means <span className="font-mono">AVAILABILITY_UNKNOWN</span> — the driver may still be assignable
          but the eligibility engine will surface a warning until a Confirmed Available window is added.
        </div>
        {canWrite && (
          <button
            data-testid="add-availability-btn"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-coach-blue text-white text-[12.5px] rounded-sm hover:bg-deep-road-navy"
          >
            <Plus size={13} /> Add Window
          </button>
        )}
      </div>

      <div className="bg-white border border-coach-silver rounded-md overflow-x-auto">
        {rows === null ? (
          <div className="p-6 text-center text-steel text-[13px]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-steel text-[13px]" data-testid="availability-empty">
            No availability windows on file. Add one to affirm this driver's availability.
          </div>
        ) : (
          <table className="w-full text-[13px]" data-testid="availability-table">
            <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
              <tr>
                <th className="text-left px-4 py-2 w-32">Start</th>
                <th className="text-left px-4 py-2 w-32">End</th>
                <th className="text-left px-4 py-2 w-44">Status</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Reason / Notes</th>
                <th className="text-left px-4 py-2 hidden md:table-cell w-32">Source</th>
                {canWrite && <th className="w-24 px-4 py-2"></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`availability-row-${r.id}`}
                    className="border-t border-coach-silver/60 hover:bg-road-white">
                  <td className="px-4 py-2 tabular font-mono text-[12px]">{r.start_date}</td>
                  <td className="px-4 py-2 tabular font-mono text-[12px]">{r.end_date}</td>
                  <td className="px-4 py-2"><StatusChip status={r.status} /></td>
                  <td className="px-4 py-2 text-steel hidden md:table-cell">
                    {r.reason ? <div className="text-carbon">{r.reason}</div> : null}
                    {r.notes && <div className="text-[11px] text-steel">{r.notes}</div>}
                    {!r.reason && !r.notes && "—"}
                  </td>
                  <td className="px-4 py-2 text-steel hidden md:table-cell">
                    {SOURCE_LABEL[r.source] || r.source}
                  </td>
                  {canWrite && (
                    <td className="px-4 py-2 text-right">
                      <button
                        data-testid={`availability-edit-${r.id}`}
                        onClick={() => setEditing(r)}
                        className="inline-flex items-center gap-1 text-[12px] text-coach-blue hover:underline mr-3">
                        <Pencil size={12} /> Edit
                      </button>
                      <button
                        data-testid={`availability-delete-${r.id}`}
                        onClick={() => remove(r)}
                        className="inline-flex items-center gap-1 text-[12px] text-critical-red hover:underline">
                        <Trash2 size={12} /> Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(creating || editing) && (
        <AvailabilityDialog
          driverId={driverId}
          initial={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={async () => { setCreating(false); setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function AvailabilityDialog({ driverId, initial, onClose, onSaved }) {
  const [status, setStatus] = useState(initial?.status || "confirmed_available");
  const [startDate, setStartDate] = useState(initial?.start_date || "");
  const [endDate, setEndDate] = useState(initial?.end_date || "");
  const [reason, setReason] = useState(initial?.reason || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [source, setSource] = useState(initial?.source || "operator");
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial;

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!startDate || !endDate) return toast.error("Start and end dates are required");
    if (endDate < startDate) return toast.error("End date must be on or after start date");
    if (status === "unavailable" && !reason.trim()) {
      return toast.error("Reason is required for an Unavailable window");
    }
    setBusy(true);
    try {
      const payload = {
        driver_id: driverId,
        start_date: startDate,
        end_date: endDate,
        status,
        reason: reason.trim() || null,
        notes: notes.trim() || null,
        source,
      };
      if (isEdit) {
        await driverAvailabilityApi.patch(driverId, initial.id, payload);
        toast.success("Availability window updated");
      } else {
        await driverAvailabilityApi.create(driverId, payload);
        toast.success("Availability window added");
      }
      await onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-carbon/60 flex items-center justify-center px-4"
         data-testid="availability-dialog"
         onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-md w-full max-w-md border border-coach-silver">
        <div className="bg-carbon text-white px-4 py-3 flex items-center justify-between">
          <div className="text-[14px]">{isEdit ? "Edit Availability Window" : "Add Availability Window"}</div>
          <button type="button" onClick={onClose} className="text-coach-silver hover:text-white">
            <X size={14} />
          </button>
        </div>
        <div className="p-4 space-y-3 text-[13px]">
          <label className="block">
            <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1">Status</div>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
                    data-testid="availability-status-select"
                    className="w-full border border-coach-silver rounded-sm px-2 py-1.5 bg-white">
              <option value="confirmed_available">Confirmed Available</option>
              <option value="unavailable">Unavailable</option>
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1">Start</div>
              <input type="date" required value={startDate} onChange={(e) => setStartDate(e.target.value)}
                     data-testid="availability-start"
                     className="w-full border border-coach-silver rounded-sm px-2 py-1.5" />
            </label>
            <label className="block">
              <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1">End</div>
              <input type="date" required value={endDate} onChange={(e) => setEndDate(e.target.value)}
                     data-testid="availability-end"
                     className="w-full border border-coach-silver rounded-sm px-2 py-1.5" />
            </label>
          </div>
          <label className="block">
            <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1">
              Reason {status === "unavailable" && <span className="text-critical-red">*</span>}
            </div>
            <input value={reason} onChange={(e) => setReason(e.target.value)}
                   placeholder={status === "unavailable" ? "Required — e.g. Vacation, PTO, Family Event" : "Optional"}
                   data-testid="availability-reason"
                   className="w-full border border-coach-silver rounded-sm px-2 py-1.5" />
          </label>
          <label className="block">
            <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1">Notes</div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      data-testid="availability-notes"
                      className="w-full border border-coach-silver rounded-sm px-2 py-1.5" />
          </label>
          <label className="block">
            <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1">Source</div>
            <select value={source} onChange={(e) => setSource(e.target.value)}
                    data-testid="availability-source"
                    className="w-full border border-coach-silver rounded-sm px-2 py-1.5 bg-white">
              <option value="operator">Operator</option>
              <option value="driver_self">Driver Self-Reported</option>
              <option value="import">Imported</option>
            </select>
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-coach-silver bg-road-white rounded-b-md">
          <button type="button" onClick={onClose} disabled={busy}
                  className="px-3 py-1.5 text-[12.5px] border border-coach-silver rounded-sm text-steel hover:bg-white">
            Cancel
          </button>
          <button type="submit" disabled={busy}
                  data-testid="availability-submit"
                  className="px-3 py-1.5 text-[12.5px] bg-coach-blue text-white rounded-sm hover:bg-deep-road-navy disabled:opacity-50">
            {busy ? "Saving…" : isEdit ? "Save Changes" : "Add Window"}
          </button>
        </div>
      </form>
    </div>
  );
}
